/**
 * Community Garden — Component Cleanup Verification Utility
 * 
 * RISK-009: Memory Leak Prevention
 * Centralized tracking and verification of component cleanup
 */

export interface CleanupVerificationResult {
  componentName: string;
  cleaned: boolean;
  errors: string[];
}

export interface ComponentCleanupRegistry {
  register(componentName: string, cleanupFn: () => void): void;
  unregister(componentName: string): void;
  verify(componentName: string): CleanupVerificationResult;
  verifyAll(): CleanupVerificationResult[];
  cleanupAll(): void;
}

class ComponentCleanupRegistryImpl implements ComponentCleanupRegistry {
  private registeredComponents = new Map<string, () => void>();
  private cleanedComponents = new Set<string>();

  register(componentName: string, cleanupFn: () => void): void {
    this.registeredComponents.set(componentName, cleanupFn);
    this.cleanedComponents.delete(componentName);
  }

  unregister(componentName: string): void {
    this.registeredComponents.delete(componentName);
    this.cleanedComponents.delete(componentName);
  }

  markCleaned(componentName: string): void {
    this.cleanedComponents.add(componentName);
  }

  verify(componentName: string): CleanupVerificationResult {
    const errors: string[] = [];
    
    if (!this.registeredComponents.has(componentName)) {
      errors.push(`Component ${componentName} is not registered`);
    }
    
    const isCleaned = this.cleanedComponents.has(componentName);
    
    return {
      componentName,
      cleaned: isCleaned,
      errors
    };
  }

  verifyAll(): CleanupVerificationResult[] {
    const results: CleanupVerificationResult[] = [];

    Array.from(this.registeredComponents.keys()).forEach(componentName => {
      results.push(this.verify(componentName));
    });

    return results;
  }

  cleanupAll(): void {
    Array.from(this.registeredComponents.entries()).forEach(([componentName, cleanupFn]) => {
      try {
        cleanupFn();
        this.markCleaned(componentName);
      } catch (error) {
        console.error(`[Cleanup] Error cleaning up ${componentName}:`, error);
      }
    });
  }

  getUncleanedComponents(): string[] {
    const uncleaned: string[] = [];
    Array.from(this.registeredComponents.keys()).forEach(componentName => {
      if (!this.cleanedComponents.has(componentName)) {
        uncleaned.push(componentName);
      }
    });
    return uncleaned;
  }
}

// Singleton instance
export const cleanupRegistry = new ComponentCleanupRegistryImpl();

/**
 * Mixin to add automatic cleanup registration to a component
 * Usage: class MyComponent extends withCleanup(BaseComponent) { ... }
 */
export function withCleanup<T extends new (...args: any[]) => any>(BaseClass: T) {
  return class extends BaseClass {
    private _componentName: string;
    private _cleanupRegistered = false;

    constructor(...args: any[]) {
      super(...args);
      this._componentName = this.constructor.name;
    }

    protected registerCleanup(cleanupFn: () => void): void {
      cleanupRegistry.register(this._componentName, cleanupFn);
      this._cleanupRegistered = true;
    }

    destroy(): void {
      // Call the parent destroy first
      if (super.destroy) {
        super.destroy();
      }
      
      // Mark as cleaned
      cleanupRegistry.markCleaned(this._componentName);
    }

    verifyCleanup(): CleanupVerificationResult {
      return cleanupRegistry.verify(this._componentName);
    }
  };
}

/**
 * Helper to verify no memory leaks before navigation
 */
export function verifyNoMemoryLeaks(): boolean {
  const uncleaned = cleanupRegistry.getUncleanedComponents();
  
  if (uncleaned.length > 0) {
    console.warn('[Cleanup] Uncleaned components detected:', uncleaned);
    return false;
  }
  
  return true;
}

/**
 * Check for common DOM leaks
 */
export function checkForDOMLeaks(): string[] {
  const leaks: string[] = [];
  
  // Check for orphaned event listeners (approximate)
  const allElements = document.querySelectorAll('*');
  const orphanedElements: string[] = [];
  
  // Check for injected styles that might not be cleaned up
  const injectedStyles = document.querySelectorAll('style[id$="-styles"]');
  const orphanedStyles: string[] = [];
  
  injectedStyles.forEach(style => {
    // Check if corresponding component still exists
    const styleId = style.id;
    const baseName = styleId.replace('-styles', '');
    
    // Heuristic: if no elements with that class exist, it might be orphaned
    const hasElements = document.querySelectorAll(`.${baseName}`).length > 0 ||
                       document.querySelectorAll(`[class*="${baseName}"]`).length > 0;
    
    if (!hasElements) {
      orphanedStyles.push(styleId);
    }
  });
  
  if (orphanedStyles.length > 0) {
    leaks.push(`Orphaned styles: ${orphanedStyles.join(', ')}`);
  }
  
  return leaks;
}

/**
 * Report memory status for debugging
 */
export function reportMemoryStatus(): void {
  if (import.meta.env.DEV) {
    if (typeof performance !== 'undefined' && (performance as any).memory) {
      const memory = (performance as any).memory;
      console.log('[Memory] Used:', Math.round(memory.usedJSHeapSize / 1048576), 'MB');
      console.log('[Memory] Total:', Math.round(memory.totalJSHeapSize / 1048576), 'MB');
      console.log('[Memory] Limit:', Math.round(memory.jsHeapSizeLimit / 1048576), 'MB');
    }
    
    const verification = cleanupRegistry.verifyAll();
    const uncleaned = verification.filter(v => !v.cleaned);
    
    if (uncleaned.length > 0) {
      console.log('[Cleanup] Uncleaned components:', uncleaned.map(u => u.componentName));
    } else {
      console.log('[Cleanup] All components cleaned up');
    }
    
    const domLeaks = checkForDOMLeaks();
    if (domLeaks.length > 0) {
      console.log('[Cleanup] DOM leaks:', domLeaks);
    }
  }
}
